<template>
	<section
		class="page page-place-gallery-item"
		v-bind:class="{ 'has-scrolled': hasScrolled }"
		v-bind:key="venueModel"
	>
		<ChalkyUiPageHero
			class="collapsed"
			description="Community Photo"
			v-bind:imageUrl="heroImageUrl"
			v-bind:title="venueModel.getName()"
			v-if="venueModel.uniqueKey"
		/>

		<ChalkyUtilityTabGroup v-bind:tabs="['Community']" v-if="venueModel.uniqueKey" v-slot="{ activeTab }">
			<div v-show="activeTab === 'community'">
				<section class="image-container">
					<picture>
						<img class="rounded" v-bind:src="mediaModel.getUrlMedium()" />
					</picture>

					<div class="d-flex">
						<ChalkyUserAttribution v-bind:key="mediaModel.uniqueKey" v-bind:userModel="mediaModel.user" />

						<ChalkyReactionFavorite v-bind:model="mediaModel" />
					</div>
				</section>

				<!-- Action buttons -->
				<section class="action-bar">
					<button class="button-secondary" v-on:click="Handle_OnClickViewComments">
						<i class="fa fa-comment"></i>
						&nbsp; Comments ({{ mediaModel.comments?.length || 0 }})
					</button>
				</section>

				<!-- Comment modal -->
				<ChalkyUtilityModal
					class="default-modal"
					v-model="isCommentModalVisible"
					v-bind:closeOnBackdropClick="true"
					v-bind:closeOnEscape="true"
					v-bind:showCloseButton="true"
				>
					<ChalkyCommentList
						direction="asc"
						v-bind:canReact="false"
						v-bind:key="mediaModel.uniqueKey"
						v-bind:model="mediaModel"
					/>
				</ChalkyUtilityModal>
			</div>
		</ChalkyUtilityTabGroup>
	</section>
</template>

<script lang="ts">
	import ChalkySticks from '@chalkysticks/sdk';
	import PageBase from '@/Core/Page.vue';
	import { Component } from 'vue-property-decorator';
	import { mounted } from '@/Utility/Decorators';

	/**
	 * There's a problem here because community images are technically
	 * on the user model and tagged to the venue, so here we don't technically
	 * know where to look.
	 *
	 * @author Matt Kenefick <matt@chalkysticks.com>
	 * @package Page
	 * @project ChalkySticks App
	 */
	@Component
	export default class PagePlaceGalleryItem extends PageBase {
		/**
		 * URL of the hero image derived from the uploaded photo
		 *
		 * @type string
		 */
		protected heroImageUrl: string = '';

		/**
		 * Whether the comment modal is visible
		 *
		 * @type boolean
		 */
		protected isCommentModalVisible: boolean = false;

		/**
		 * @type ChalkySticks.Model.Media
		 */
		protected mediaModel: ChalkySticks.Model.Media = new ChalkySticks.Model.Media();

		/**
		 * @type ChalkySticks.Model.Venue
		 */
		protected venueModel: ChalkySticks.Model.Venue = ChalkySticks.Factory.Venue.model();

		/**
		 * @return Promise<void>
		 */
		@mounted
		protected async setup(): Promise<void> {
			// Venue fetch
			this.venueModel
				.set({
					id: this.$route.params.id,
				})
				.fetch();

			// Media fetch
			this.mediaModel
				.set({
					id: this.$route.params.mediaId,
				})
				.setQueryParam('source', 'tagged')
				.setQueryParam('include', 'user')
				.useModifiedEndpoint(this.venueModel);

			// Fetch venue if not
			await this.mediaModel.fetch();

			// Use the uploaded photo as the hero background
			this.heroImageUrl = this.mediaModel.getUrlMedium() || '';
		}

		// region: Event Handlers
		// ---------------------------------------------------------------------------

		/**
		 * @param PointerEvent e
		 * @return Promise<void>
		 */
		protected async Handle_OnClickViewComments(e: PointerEvent): Promise<void> {
			e.preventDefault();

			this.isCommentModalVisible = true;
		}

		// endregion: Event Handlers
	}
</script>

<style lang="scss">
	.page-place-gallery-item {
		.ui-pagehero figure::before {
			background: linear-gradient(34deg, rgba(165, 63, 66, 1) 18%, rgba(165, 63, 66, 0) 67%);
			content: ' ';
			height: 100%;
			opacity: 0.75;
			pointer-events: none;
			position: absolute;
			width: 100%;
		}

		.action-bar {
			align-items: center;
			display: flex;
			gap: 1rem;
			justify-content: center;
		}

		.chalky.reaction-favorite button {
			margin-right: 1rem;
			padding: 0;
		}

		.utility-gallery.grid .gallery-item {
			aspect-ratio: 1 / 1;
		}

		.utility-modal.default-modal .modal-content {
			overflow: hidden;
			padding: 0;

			.modal-body {
				height: 100%;
			}

			.chalky.comment-list {
				height: 100%;
				margin: 0;
				overflow: auto;
				padding: 1rem;

				.comment-container {
					flex: 1;
				}
			}
		}
	}

	// Animation
	// ---------------------------------------------------------------------------

	.page-place-gallery-item {
		.utility-modal.state-visible .comment-list {
			animation: slide-top 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94) both;

			@keyframes slide-top {
				0% {
					opacity: 0;
					transform: translateY(50px);
				}

				100% {
					opacity: 1;
					transform: translateY(0px);
				}
			}
		}
	}
</style>
